import React, { useEffect, useState, useMemo } from 'react';
import { getMovementsByJob } from '../../redux/api';
import {
  getOpenWithdrawnMovements,
  getWithdrawnMovementLabel,
  getWithdrawnMovementSourceLabel,
  WITHDRAWN_STATUS_LABELS,
} from '../../utils/withdrawnItemsUtils';

/**
 * Collapsible picker for job-completion: select withdrawn materials,
 * set used qty + selling price. Unused qty returns to stock on submit.
 */
const JobWithdrawnMaterialsPicker = ({
  jobId,
  selected = {},
  onChange,
  open = false,
  onOpenChange,
  className = '',
}) => {
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!jobId) {
      setMovements([]);
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const response = await getMovementsByJob(jobId);
        if (!cancelled) setMovements(response.data || []);
      } catch {
        if (!cancelled) setMovements([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  const openMovements = useMemo(() => getOpenWithdrawnMovements(movements), [movements]);
  const selectedCount = Object.keys(selected).length;

  const updateSelection = (movementId, patch) => {
    const next = { ...selected };
    if (patch === null) {
      delete next[movementId];
    } else {
      next[movementId] = { ...(next[movementId] || {}), ...patch };
    }
    onChange?.(next);
  };

  const toggleMovement = (movement) => {
    const id = movement._id;
    if (selected[id]) {
      updateSelection(id, null);
      return;
    }
    updateSelection(id, {
      movementId: id,
      movement,
      quantity: Number(movement.quantity) || 1,
      unitSellingPrice: Number(movement.unitSellingPrice) || 0,
      unitCost: Number(movement.unitCost) || 0,
      maxQuantity: Number(movement.quantity) || 1,
      label: getWithdrawnMovementLabel(movement),
      unit: movement.unit || 'kom',
    });
  };

  if (!jobId) return null;

  if (!open) {
    const label =
      openMovements.length === 0 && !loading
        ? 'Nema povučene robe za ovaj posao'
        : selectedCount > 0
          ? `Povučena roba (${selectedCount} izabrano)`
          : loading
            ? 'Povučena roba…'
            : `Prikaži povučenu robu${openMovements.length ? ` (${openMovements.length})` : ''}`;

    return (
      <button
        type="button"
        onClick={() => onOpenChange?.(true)}
        disabled={loading || openMovements.length === 0}
        className={`w-full px-2 py-2 bg-gray-800 border border-gray-600 rounded text-xs text-gray-200 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
      >
        {label}
      </button>
    );
  }

  return (
    <div className={className}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <p className="text-[11px] text-gray-400">
          Čekiraj utrošeno · promeni količinu i prodajnu cenu · ostatak se vraća
        </p>
        <button
          type="button"
          onClick={() => onOpenChange?.(false)}
          className="shrink-0 px-2 py-1 text-gray-300 text-xs bg-gray-800 border border-gray-600 rounded hover:bg-gray-700"
          title="Sakrij listu"
        >
          ×
        </button>
      </div>

      {loading ? (
        <div className="text-center py-4 text-gray-400 text-xs bg-gray-700 rounded-lg">Učitavanje...</div>
      ) : openMovements.length === 0 ? (
        <div className="bg-gray-700 rounded-lg p-3 text-center text-gray-400 text-xs">
          Nema povučene robe za ovaj posao
        </div>
      ) : (
        <ul className="bg-gray-700 rounded-lg p-3 space-y-2 max-h-56 overflow-y-auto">
          {openMovements.map((movement) => {
            const id = movement._id;
            const isSelected = Boolean(selected[id]);
            const row = selected[id];
            const maxQty = Number(movement.quantity) || 1;
            const unused =
              isSelected && row ? Math.max(0, maxQty - (Number(row.quantity) || 0)) : 0;

            return (
              <li key={id} className="bg-gray-600 rounded p-2">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleMovement(movement)}
                    className="mt-1 w-4 h-4 text-amber-500 bg-gray-800 border-gray-600 rounded focus:ring-amber-500"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-white text-xs font-medium truncate">
                      {getWithdrawnMovementLabel(movement)}
                    </div>
                    <div className="text-[11px] text-gray-400 mt-0.5">
                      {getWithdrawnMovementSourceLabel(movement)}
                      {' · '}
                      {maxQty} {movement.unit || 'kom'}
                      {' · '}
                      {WITHDRAWN_STATUS_LABELS[movement.status] || movement.status}
                    </div>
                  </div>
                </label>

                {isSelected && row && (
                  <div className="mt-2 ml-6 grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[10px] text-gray-400 mb-0.5">
                        Utrošeno (max {maxQty})
                      </label>
                      <input
                        type="number"
                        min="0.01"
                        max={maxQty}
                        step="0.01"
                        value={row.quantity}
                        onChange={(e) => {
                          const value = e.target.value;
                          const num = Number(value);
                          updateSelection(id, {
                            quantity:
                              value === ''
                                ? ''
                                : Number.isFinite(num)
                                  ? Math.min(maxQty, Math.max(0.01, num))
                                  : row.quantity,
                          });
                        }}
                        className="w-full h-8 rounded border border-gray-500 bg-gray-700 px-2 text-xs text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] text-gray-400 mb-0.5">
                        Prodajna / kom
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={row.unitSellingPrice}
                        onChange={(e) => {
                          const value = e.target.value;
                          updateSelection(id, {
                            unitSellingPrice: value === '' ? '' : Number(value),
                          });
                        }}
                        className="w-full h-8 rounded border border-gray-500 bg-gray-700 px-2 text-xs text-white"
                      />
                    </div>
                    {unused > 0 && (
                      <p className="col-span-2 text-[10px] text-blue-300">
                        Vratiće se: {unused} {movement.unit || 'kom'}
                      </p>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default JobWithdrawnMaterialsPicker;
